import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    ActivatedRoute,
    Router,
} from '@angular/router';
import {
    AppStateFacade,
    Setting,
    UserAuthState,
} from '@app-state';
import {
    ErrorCodes,
    INotificationSettings,
    IUser,
    IUserProfile,
    UserType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { UiUserProfileGeneralComponent } from '@pages/profile/user-profile-general.component';
import { INotificationPreferenceChanged } from '@pages/profile/user-profile-notifications.component';
import { restrictedUrlTree } from '@pages/shared/restricted-access';
import {
    AuthService,
    ModalService,
    SignalRService,
    UserService,
} from '@services';
import { DEFAULT_NOTIFICATION_SETTINGS } from '@services/user.service';
import { unsubscribe } from '@utils/object.utils';

import cloneDeep from 'lodash-es/cloneDeep';
import startCase from 'lodash-es/startCase';
import {
    BehaviorSubject,
    Observable,
    Subscription,
} from 'rxjs';
import {
    finalize,
    map,
} from 'rxjs/operators';

@Component({
    templateUrl: './profile.page.html',
    styleUrls: ['./profile.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilePageComponent implements OnInit, OnDestroy {
    public readonly isViewOnly = !!this._appState.settings.get(Setting.isCloudRPAInstanceEnabled);
    public viewedUser = {} as IUser;
    public userId!: number;
    public isHost = this._appState.user.loggedInAsHost;
    public loading = {
        user$: new BehaviorSubject(false),
        password$: new BehaviorSubject(false),
        notifications: {} as Record<string, boolean>,
    };
    public notificationSettings = {} as INotificationSettings;
    public notificationSettingsPermissions = {} as INotificationSettings;
    public UserType = UserType;

    public get isEditable$(): Observable<boolean> {
        return this._appState.user.permissions
            .isGranted('Users.Edit')
            .pipe(
                map((result) => {
                    return result || this.isActiveUser;
                }),
                map(result => result),
            );
    }

    public get isActiveUser() {
        return this.userId === this._appState.user.value!.id;
    }

    public get isDirectoryEntity() {
        return [UserType.DirectoryGroup, UserType.DirectoryUser].includes(this.viewedUser.type);
    }

    public disallowEmailChange = false;
    public disallowPasswordChange = false;

    @ViewChild('general', { static: true })
    private _general!: UiUserProfileGeneralComponent;
    private _routeParamSub!: Subscription;
    private _userSub!: Subscription;
    private _currentUserNotificationPermissions = {} as INotificationSettings;

    constructor(
        public userService: UserService,
        private _appState: AppStateFacade,
        private _cd: ChangeDetectorRef,
        private _modalService: ModalService,
        private _authService: AuthService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _translateService: TranslateService,
        private _signalRService: SignalRService,
    ) {
        this.disallowEmailChange = !this._appState.authConfig.value.cloud.allowSelfEmailUpdate;
        this.disallowPasswordChange = !this._appState.authConfig.value.cloud.allowPasswordChange;
    }

    ngOnInit() {
        if (this._appState.user.authState === UserAuthState.LoggedInAsTenant) {
            Object.keys(DEFAULT_NOTIFICATION_SETTINGS).forEach(key => {
                (this as any)[`_${key}PermsSub`] =
                    this._appState.user.permissions
                        .isGranted(`${startCase(key)}.View`)
                        .subscribe(isGranted => this._notificationPermissionUpdate(isGranted, key as keyof INotificationSettings));
            });
        }

        this._routeParamSub = this._route
            .params
            .subscribe(this._routeParamUpdate);
    }

    ngOnDestroy() {
        unsubscribe(
            this._routeParamSub,
            this._userSub,
        );
    }

    public onEnterUserViewMode() {
        this.viewedUser = cloneDeep(this._appState.user.value!);
    }

    public onSubmitUser(user: IUserProfile) {
        const payload = cloneDeep(user);
        let patchUser$: Observable<any>;

        this.loading.user$.next(true);

        if (this.disallowEmailChange) {
            delete payload['emailAddress'];
        }

        if (this.isActiveUser) {
            patchUser$ = this._appState.user.patch(payload);
        } else {
            patchUser$ = this.userService.patch(this.viewedUser.id, payload);
        }

        patchUser$
            .pipe(
                finalize(
                    () => this.loading.user$.next(false),
                ),
            )
            .subscribe(() => {
                this._general.formGroup.reset(this._general.formGroup.value);
                this._general.viewMode(false);
            });
    }

    public onSubmitPassword(password: { current: string; new: string; }) {
        this._modalService.onConfirm(
            null,
            () => {
                this.loading.password$.next(true);

                this.userService
                    .changePassword(this._appState.user.value!.id, {
                        currentPassword: password.current,
                        newPassword: password.new,
                    })
                    .pipe(
                        finalize(
                            () => this.loading.password$.next(false),
                        ),
                    )
                    .subscribe(
                        () => {
                            this._signalRService.disconnect();
                            this._authService.logout();
                        },
                        (response) => {
                            if (
                                response.error &&
                                response.error.errorCode === ErrorCodes.UserIsLockedOut
                            ) {
                                this._authService.logout();
                            }
                        },
                    );
            },
            () => { },
            this._translateService.instant('user_modal_password_warning'),
        );
    }

    public updateNotifications(notification: INotificationPreferenceChanged) {
        const notificationSettings = this.notificationSettings;
        let update$: Observable<any>;

        notificationSettings[notification.key as keyof INotificationSettings] = notification.enabled;

        this.loading.notifications[notification.key] = true;

        if (this.isActiveUser) {
            update$ = this._appState.user.patch({ notificationSettings });
        } else {
            update$ = this.userService.patch(this.userId, { notificationSettings });
        }

        update$
            .pipe(
                finalize(
                    () => {
                        const state: Record<string, boolean> = {};
                        state[notification.key] = false;
                        this.loading.notifications = Object.assign({}, this.loading.notifications, state);
                        this._cd.markForCheck();
                    }),
            )
            .subscribe();
    }

    private _notificationPermissionUpdate = (isGranted: boolean, key: keyof INotificationSettings) => {
        this._currentUserNotificationPermissions[key] = isGranted;

        // trigger change detection if needed
        if (this._currentUserNotificationPermissions === this.notificationSettingsPermissions) {
            this.notificationSettingsPermissions =
                this._currentUserNotificationPermissions =
                cloneDeep(this._currentUserNotificationPermissions);
        }
        this._cd.markForCheck();
    }

    private _routeParamUpdate = (params: { [x: string]: string; }) => {
        this._general.viewMode(false);

        this.userId = typeof params['id'] === 'undefined' ?
            this._appState.user.value!.id :
            parseInt(params['id'], 10);

        if (isNaN(this.userId)) {
            this._redirect();
        }

        if (!this.isActiveUser) {
            if (this._userSub) { this._userSub.unsubscribe(); }

            this.loading.user$.next(true);

            this._userSub = this.userService
                .getInstance(this.userId)
                .pipe(
                    finalize(
                        () => this.loading.user$.next(false),
                    ),
                )
                .subscribe((user) => {
                    if (user.type === UserType.Robot) {
                        this._redirect();
                        return;
                    }
                    this.notificationSettings = Object.assign(
                        cloneDeep(DEFAULT_NOTIFICATION_SETTINGS),
                        user.notificationSettings,
                    );
                    this.notificationSettingsPermissions = DEFAULT_NOTIFICATION_SETTINGS;
                    this.viewedUser = user;
                    this._cd.markForCheck();
                }, () => {
                    this._redirect();
                });
        } else {
            this.viewedUser = cloneDeep(this._appState.user.value!);
            this.notificationSettings = Object.assign(
                cloneDeep(DEFAULT_NOTIFICATION_SETTINGS),
                this._appState.user.value!.notificationSettings,
            );
            this.notificationSettingsPermissions = this._currentUserNotificationPermissions;
            this._cd.markForCheck();
        }
    }

    private _redirect() {
        this._router.navigateByUrl(restrictedUrlTree(this._router));
    }
}
